<script setup>
import DpExpand from "./DpExpand";
import ApplicationSwitchMenu from "./DpHeader/ApplicationSwitchMenu";
import AvatarMenu from "./DpHeader/AvatarMenu";
import Avatar from "./DpHeader/Avatar";
import DpWrapper from "./DpWrapper";


import { computed } from "vue";

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

import { useEnvStore } from '@dutypay/store-modules'

const envStore = useEnvStore()
const applicationVariable = envStore.applicationVariable;
const apiUrls = envStore.apiUrls;

const assetsApiEndpoint = computed(() => {
  return `${ apiUrls.dutypay }/api/v0/assets`;
});
</script>

<script>
import { mapState } from 'pinia';
import { useCompanyStore } from '@dutypay/store-modules'


import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

export default {
  name: "DpHeader",
  props: {
    menuItems: { type: Array },
    light: { type: Boolean, default: false },
    hasCompanySwitch: { type: Boolean, default: true }
  },
  data() {
    return {
      avatarMenuVisible: false,
      applicationSwitchMenu: false,
      applicationSwitchMenuVisible: false,
      mobileMenuVisible: false,
      companySelectVisible: false,
      latestKnownScrollY: 0,
      ticking: false,
      dpApplicationLogoHeight: 20,
      applicationSwitchLogoHeight: 15,
      elHeaderMargin: 0,
    };
  },
  computed: {
    ...mapState(useCompanyStore, ['selectedCompany', 'hasSelectedCompany', 'areCompaniesFetched', 'companiesCountTotal']),
    isEmpty() {
      return isEmpty;
    },
    get() {
      return get;
    },
    subHeaderMenuItems() {
      if (this.$route.matched[0] === undefined)
        return undefined;
      if (this.$route.matched[0].path === undefined)
        return undefined;
      if (find(this.menuItems, { 'path': this.$route.matched[0].path }) === undefined)
        return undefined;
      if (find(this.menuItems, ['path', this.$route.matched[0].path]).children === undefined)
        return undefined;
      return find(this.menuItems, ['path', this.$route.matched[0].path]).children || [];
    },
  },
  methods: {
    matchWithActiveRoute(path) {
      return !!(find(this.$route.matched, (route) => {
        return route.path === path;
      }));
    },
    handleMenuItemClick(item) {
      this.$router.push(item.path);
      this.hideMobileMenu();
      this.hideAvatarMenu();
      this.hideCompanySelect();
    },
    handleClickOutside(e) {
      const el = this.$refs.headerContainer;
      if (!el.contains(e.target)) {
        this.hideMobileMenu();
        this.hideAvatarMenu();
        this.hideCompanySelect();
        this.hideApplicationSwitchMenu();
      }
    },
    hideAll() {
      this.hideMobileMenu();
      this.hideAvatarMenu();
      this.hideApplicationSwitchMenu();
    },
    showMobileMenu() {
      this.hideAll();
      this.mobileMenuVisible = true;
    },
    hideMobileMenu() {
      this.mobileMenuVisible = false;
    },
    showAvatarMenu() {
      this.hideAll();
      this.avatarMenuVisible = true;
    },
    hideAvatarMenu() {
      this.avatarMenuVisible = false;
    },
    activateApplicationSwitch() {
      this.applicationSwitchMenu = true
    },
    showApplicationSwitchMenu() {
      this.hideAll();
      this.applicationSwitchMenuVisible = true;
    },
    hideApplicationSwitchMenu() {
      this.applicationSwitchMenuVisible = false;
    },
    toggleAvatarMenu() {
      (this.avatarMenuVisible) ? this.hideAvatarMenu() : this.showAvatarMenu();
    },
    toggleMobileMenu() {
      (this.mobileMenuVisible) ? this.hideMobileMenu() : this.showMobileMenu();
    },
    toggleApplicationSwitchMenu() {
      (this.applicationSwitchMenuVisible) ? this.hideApplicationSwitchMenu() : this.showApplicationSwitchMenu();
    },
    routeToEntryPoint() {
      this.$router.push({ path: '/' });
    },
    showCompanySelect() {
      if (this.areCompaniesFetched && this.companiesCountTotal <= 1)
        return;
      this.companySelectVisible = true;
    },
    hideCompanySelect() {
      this.companySelectVisible = false;
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside, true)
  },
};
</script>

<template>
  <div class="dp-header" ref="headerContainer">

    <div class="dp-header__header-bar" :class="applicationVariable">
      <DpWrapper>
        <div class="dp-flex row justify-content__space-between align-items__center">
          <div class="hidden-xs-only dp-flex row flex-basis-1">
            <div v-if="applicationSwitchMenu"
                 class="header-bar__menu-items header-bar__hover dp-flex align-items__center"
                 :class="{'is-active': applicationSwitchMenuVisible}"
                 @click="toggleApplicationSwitchMenu" tabindex="0">

              <img :src="`${assetsApiEndpoint}/56cbb4a5-0fb4-4378-9577-b5fafcf68ac5/svg`"
                   alt="Application Switch" height="24" width="24" class="speak-never"/>
            </div>

            <div class="header-bar__menu-items header-bar__hover dp-flex align-items__center"
                 @click="handleMenuItemClick({path: '/', slug: 'root'})"
                 tabindex="0">
              <img
                  :src="`${assetsApiEndpoint}/logo/${applicationVariable}/${applicationVariable === 'dutypay' ? 'standard' : 'mini'}/${applicationVariable === 'dutypay' ? 'light' : 'inverted'}/svg`"
                  :alt="applicationVariable" class="header-bar__application-logo speak-never"/>
            </div>

            <div v-for="(item, index) in menuItems"
                 class="header-bar__menu-items header-bar__hover dp-flex align-items__center"
                 :class="{'is-active': matchWithActiveRoute(item.path)}"
                 :key="index"
                 :index="item.path"
                 @click="handleMenuItemClick(item)"
                 tabindex="0">
              <span class="correct-for-border">{{ item.label || '' }}</span>
            </div>
          </div>

          <div class="hidden-sm-and-up header-bar__menu-items dp-flex align-items__center dp-mobile-menu-toggle"
               @click="toggleMobileMenu">
            <div :class="{'visible': mobileMenuVisible}">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <div class="hidden-sm-and-up header-bar__menu-items header-bar__hover dp-flex align-items__center"
               @click="(applicationSwitchMenu) ? toggleApplicationSwitchMenu() : handleMenuItemClick({path: '/', slug: 'root'})"
               tabindex="0" :class="{'is-active': applicationSwitchMenuVisible}">
            <img v-if="applicationSwitchMenu" :src="`${assetsApiEndpoint}/logo/${applicationVariable}/mobile/light/svg`"
                 :alt="applicationVariable" class="header-bar__application-logo speak-never"/>
            <img v-else
                 :src="`${assetsApiEndpoint}/logo/${applicationVariable}/${applicationVariable === 'dutypay' ? 'standard' : 'mini'}/${applicationVariable === 'dutypay' ? 'light' : 'inverted'}/svg`"
                 :alt="applicationVariable" class="header-bar__application-logo speak-never"/>
          </div>

          <div class="flex-basis-0 header-bar__hover dp-flex align-items__center"
               :class="{'is-active': avatarMenuVisible ||
               matchWithActiveRoute('/account-settings') ||
               matchWithActiveRoute('/company-settings') ||
               matchWithActiveRoute('/developer-settings')}">

            <div tabindex="0"
                 @click="toggleAvatarMenu">
              <Avatar/>
            </div>

          </div>
        </div>

        <DpExpand>
          <ul v-show="mobileMenuVisible" class="header-bar__mobile-menu-items">
            <li v-for="(menuItem, menuItemIndex) in menuItems"
                :key="`mobile-menu-item-${menuItemIndex}`"
                :class="{'is-active': matchWithActiveRoute(menuItem.path),
                    'has-children': menuItem.children !== undefined}"
                @click="menuItem.children ? ()=>{} : handleMenuItemClick(menuItem)">
              <span @click="menuItem.children ? handleMenuItemClick(menuItem) : ()=>{}">{{
                  menuItem.label || ''
                }}</span>
              <ul v-if="menuItem.children">
                <li v-for="(subMenuItem, subMenuItemIndex) in menuItem.children"
                    :key="`mobile-menu-item-${menuItemIndex}-sub-menu-${subMenuItemIndex}`"
                    :class="{'is-active': matchWithActiveRoute(subMenuItem.path)}"
                    @click="handleMenuItemClick(subMenuItem)">{{ subMenuItem.label }}
                </li>
              </ul>
            </li>
          </ul>
        </DpExpand>

        <DpExpand>
          <AvatarMenu v-show="avatarMenuVisible"
                      :closeHandler="hideAvatarMenu"
                      :hasCompanySwitch="hasCompanySwitch">
          </AvatarMenu>
        </DpExpand>

        <DpExpand>
          <ApplicationSwitchMenu v-show="applicationSwitchMenuVisible"
                                 :closeHandler="hideApplicationSwitchMenu"
                                 v-on:hasApplications="activateApplicationSwitch">
          </ApplicationSwitchMenu>
        </DpExpand>

      </DpWrapper>
    </div>

    <div class="dp-header__sub-header-bar hidden-xs-only">
      <DpWrapper>
        <div class="dp-flex justify-content__space-between align-items__center">
          <div class="flex-basis-1">
            <ul class="sub-header-bar__menu-items" v-if="subHeaderMenuItems">
              <li v-for="(item, index) in subHeaderMenuItems"
                  @click="handleMenuItemClick(item)"
                  :key="index"
                  :class="{'is-active': matchWithActiveRoute(item.path)}">{{ item.label }}
              </li>
            </ul>
          </div>
          <div v-if="hasCompanySwitch" class="flex-basis-1">
            <div class="sub-header-bar__company-switch" :style="(companiesCountTotal > 1) ? {'cursor': 'pointer'} : {}">
              <div class="dp-header__sub-header-bar__company-switch__select" v-show="companySelectVisible">
                <DpSelectCompanySwitch size="small"
                                       style="width: 100%;"
                                       popper-class="dp-header__sub-header-bar__company-switch__select__popper-dropdown"
                                       v-on:switch="hideCompanySelect()"/>
              </div>
              <div @click="showCompanySelect">{{ get(selectedCompany, 'name.legal', t('selectCompany.label')) }}</div>
            </div>
          </div>
        </div>
      </DpWrapper>
    </div>

  </div>
</template>

<style scoped lang="scss">
@import '../scss_legacy/dp-color';

.dp-header {
  z-index: 3005;
  position: sticky;
  top: 0;
  max-width: 100vw;
  max-height: 100vh;
  overflow-y: scroll;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .dp-header__header-bar {
    // -------------------------
    //   Header bar colors
    // -------------------------
    @each $application, $application-color in $dp-application-color {
      &.#{$application} {
        /* Permalink - use to edit and share this gradient: https://colorzilla.com/gradient-editor/#281e69+1,0078bf+100 */
        background: map-get($application-color, "header-bar__background"); /* Old browsers */
        background: -moz-linear-gradient(11deg, map-get($application-color, "header-bar__gradient") 1%, map-get($application-color, "header-bar__background") 100%); /* FF3.6-15 */
        background: -webkit-linear-gradient(11deg, map-get($application-color, "header-bar__gradient") 1%, map-get($application-color, "header-bar__background") 100%); /* Chrome10-25,Safari5.1-6 */
        background: linear-gradient(11deg, map-get($application-color, "header-bar__gradient") 1%, map-get($application-color, "header-bar__background") 100%); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      }
    }
  }

  .dp-header__header-bar {
    /*---------------------------
    Burger Menu
    ---------------------------*/
    $mobile-menu-toggle-width: 26px;
    $mobile-menu-toggle-height: 16px;
    $mobile-menu-toggle-thickness: 2px;

    .dp-mobile-menu-toggle {
      div {
        position: relative;
        margin-top: 0.4rem;
        height: 24px;
        left: 0;
        width: $mobile-menu-toggle-width;
        cursor: pointer;
        border-bottom: none !important;

        span {
          position: absolute;
          height: $mobile-menu-toggle-thickness;
          width: $mobile-menu-toggle-width;
          background: #ffffff;
          border-radius: $mobile-menu-toggle-thickness*3;
          opacity: 1;
          left: 0;
          margin: 0;
          padding: 0;
          transform: rotate(0deg);
          transition: .25s ease-in-out;
        }

        span:nth-child(1) {
          top: 0;
        }

        span:nth-child(2),
        span:nth-child(3) {
          top: $mobile-menu-toggle-height/2;
        }

        span:nth-child(4) {
          top: $mobile-menu-toggle-height;
        }

        &.visible span:nth-child(1) {
          top: $mobile-menu-toggle-height/2;
          width: 0;
          left: $mobile-menu-toggle-width/2;
          /*opacity: 0;*/
        }

        &.visible span:nth-child(2) {
          transform: rotate(45deg);
        }

        &.visible span:nth-child(3) {
          transform: rotate(-45deg);
        }

        &.visible span:nth-child(4) {
          top: $mobile-menu-toggle-height/2;
          width: 0;
          left: $mobile-menu-toggle-width/2;
          /*opacity: 0;*/
        }
      }
    }

    ul.header-bar__mobile-menu-items {
      color: #FFFFFF;
      padding: .4rem 0 0.8rem 0;
      margin: 0;
      list-style: none;

      li {
        margin: .8rem 0 .8rem 0;
        padding: .8rem .8rem .2rem .8rem;
        border-inline-start: 2px solid transparent;

        span {
          padding-inline-start: 0.8em;
        }

        &.is-active {
          border-inline-start: 2px solid rgba(255, 255, 255, 0.9);
        }

        ul {
          margin: 1.2rem 0 0 0;
          padding: .8rem 0;
          list-style: none;
          border-radius: 8px;

          li {
            margin: 0;
            padding: 0.8rem 1rem;

            &.is-active {
              border-inline-start: 3px solid nth($dp-ui-gradient, 5);
            }
          }
        }
      }
    }

    .header-bar__menu-items {
      margin-inline-end: 1.35rem;
      color: #FFFFFF;
      font-size: 12px;

      span {
        margin-top: 2px;
      }
    }

    .header-bar__application-logo {
      max-height: 20px;
      width: auto;
      speak: never;
    }

    .header-bar__hover {
      cursor: pointer;
      height: 50px;
      border-bottom: 2px solid transparent;

      &:hover,
      &:active,
      &.is-active {
        border-bottom: 2px solid #ffffff;
      }
    }
  }

  .dp-header__sub-header-bar {
    background-color: #F7F7F7;
    color: #828282;

    font-size: 12px;
    font-weight: 700;
    vertical-align: sub;

    .sub-header-bar__menu-items {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        cursor: pointer;
        display: inline-flex;
        margin: 0;
        margin-inline-end: 1.15rem;
        padding: .8rem 0 .5rem 0;
        border-bottom: 2px solid transparent;

        &:hover,
        &:active,
        &.is-active {
          border-bottom: 2px solid nth($dp-ui-gradient, 5);
        }
      }
    }

    .sub-header-bar__company-switch {
      position: relative;
      margin-inline-start: .8em;
      padding: .8rem 0 .5rem 0;
      border-bottom: 2px solid transparent;

      .dp-header__sub-header-bar__company-switch__select {
        position: absolute;
        inset-block-start: .3rem;
        inset-inline-end: 0;
        width: 20rem;
      }
    }
  }
}

</style>

<style lang="scss">
.dp-header__sub-header-bar__company-switch__select__popper-dropdown {
  z-index: 3006 !important;
}

.dp-header__sub-header-bar__company-switch__select .el-input .el-input__inner {
  border-color: #DCDFE6;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
